import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import isEmpty from 'lodash/isEmpty';
import {
  IBookingSummary,
  ISessionKey,
  IBookPackageWithCard,
  IBookingComplete,
  ICardBooking,
  IGuestsBooking,
  IErrorField,
} from 'src/common-types/index';
import {
  SESSION_SEARCH_EXPIRED_MESSAGE,
  SESSION_EXPIRED_STATUS,
  Urls,
  VALIDATION_ERROR,
  EXTERNAL_ERROR,
  PRICE_CHANGED,
  SOLD_OUT,
  ERROR,
  STATUS_OK,
  HOTEL_BOOKING_GUEST_INFO_LABEL,
  HOTEL_BOOKING_ADDRESS_INFO_LABEL,
  ROOMS_SEARCH_LABEL,
  SESSION_KEY_LABEL_ROOMS,
  SESSION_KEY_LABEL,
  SAVING_MISMATCH,
  HOTEL_CONFIRMED_INFO,
  HOTEL_BOOKING_COMPLETE,
  HOTEL_PACKAGE_ID,
} from 'src/constants/index';
import { getHeaders, axiosInstance, UrlUtils } from 'src/utils/index';
import { AppThunk } from '../index';

export interface IReviewBookState {
  bookingSummary: IBookingSummary;
  loading: boolean;
  error: string;
  expiredSession: boolean;
  isUpdatePrice: boolean;
  isGetCoupon: boolean;
  loadingBooking: boolean;
  errorBooking: string;
  bookingComplete: boolean;
  booking: IBookingComplete;
  guests: IGuestsBooking[];
  card: ICardBooking;
  errorsField: IErrorField[];
  isExternalError: boolean;
  isPriceChangedError: boolean;
  isSoldOutError: boolean;
  errorsBookingMessage: IErrorField[];
  isBookingInProgress: boolean;
  isSavingsMismatch: boolean;
}

const initialState: IReviewBookState = {
  bookingSummary: null,
  loading: false,
  error: '',
  expiredSession: false,
  isUpdatePrice: false,
  isGetCoupon: false,
  loadingBooking: false,
  errorBooking: '',
  bookingComplete: false,
  booking: null,
  guests: null,
  card: {
    addressLine: '',
    cvv: '',
    cardNumber: '',
    cardType: null,
    city: '',
    country: 'US',
    state: '',
    expireDate: '',
    holderName: '',
    phone: '',
    zipCode: '',
  },
  errorsField: null,
  isExternalError: false,
  isPriceChangedError: false,
  isSoldOutError: false,
  errorsBookingMessage: null,
  isBookingInProgress: false,
  isSavingsMismatch: false,
};

const zeroItem = 0;

const reviewBookSlice = createSlice({
  name: 'review-book',
  initialState,
  reducers: {
    setLoadingBook: (state: IReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    setErrorBook: (state: IReviewBookState, { payload }: PayloadAction<string>) => {
      state.error = payload;
    },
    setReviewBook: (state: IReviewBookState, { payload }: PayloadAction<IBookingSummary>) => {
      state.bookingSummary = payload;
    },
    setExpiredSession: (state: IReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.expiredSession = payload;
    },
    setSavingsMismatch: (state: IReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.isSavingsMismatch = payload;
    },
    setUpdatePriceReview: (state: IReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.isUpdatePrice = payload;
    },
    setGetCouponReview: (state: IReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.isGetCoupon = payload;
    },
    setLoadingBooking: (state: IReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.loadingBooking = payload;
    },
    setErrorBooking: (state: IReviewBookState, { payload }: PayloadAction<string>) => {
      state.errorBooking = payload;
    },
    setBookingComplete: (state: IReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.bookingComplete = payload;
    },
    setBooking: (state: IReviewBookState, { payload }: PayloadAction<IBookingComplete>) => {
      state.booking = payload;
    },
    setGuests: (state: IReviewBookState, { payload }: PayloadAction<IGuestsBooking[]>) => {
      state.guests = payload;
    },
    setCard: (state: IReviewBookState, { payload }: PayloadAction<ICardBooking>) => {
      state.card = payload;
    },
    setErrorsField: (state: IReviewBookState, { payload }: PayloadAction<IErrorField[]>) => {
      state.errorsField = payload;
    },
    setExternalError: (state: IReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.isExternalError = payload;
    },
    setPriceChangedError: (state: IReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.isPriceChangedError = payload;
    },
    setSoldOutError: (state: IReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.isSoldOutError = payload;
    },
    setErrorsBookingMessage: (
      state: IReviewBookState,
      { payload }: PayloadAction<IErrorField[]>,
    ) => {
      state.errorsBookingMessage = payload;
    },
    setBookingInProgress: (state: IReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.isBookingInProgress = payload;
    },
    resetState: () => {
      return initialState;
    },
  },
});

export const {
  setLoadingBook,
  setErrorBook,
  setReviewBook,
  setExpiredSession,
  setUpdatePriceReview,
  setGetCouponReview,
  setLoadingBooking,
  setErrorBooking,
  setBookingComplete,
  setBooking,
  setGuests,
  setCard,
  setErrorsField,
  setExternalError,
  setPriceChangedError,
  setSoldOutError,
  setErrorsBookingMessage,
  setBookingInProgress,
  resetState,
  setSavingsMismatch,
} = reviewBookSlice.actions;

export const reviewBookReducer = reviewBookSlice.reducer;

export const getReviewBook = (
  hotelId: number,
  packageId: string,
  sessionKey: ISessionKey,
): AppThunk => {
  return async (dispatch, getState) => {
    const { reviewBookStore } = getState();
    dispatch(setExpiredSession(false));

    if (!reviewBookStore.isGetCoupon) {
      dispatch(setLoadingBook(true));
    }

    dispatch(setUpdatePriceReview(true));

    try {
      const res = await axiosInstance.post(
        Urls.BookingSummary,
        { hotelId, packageId, sessionKey },
        { ...getHeaders() },
      );
      dispatch(setUpdatePriceReview(false));
      dispatch(setLoadingBook(false));

      if (
        res.data?.bookingCard?.package?.packageId !==
        JSON.parse(localStorage.getItem(HOTEL_PACKAGE_ID))
      ) {
        localStorage.removeItem(HOTEL_CONFIRMED_INFO);
        localStorage.removeItem(HOTEL_BOOKING_COMPLETE);
        localStorage.removeItem(HOTEL_PACKAGE_ID);
      }

      dispatch(setReviewBook(res.data));
      dispatch(setGetCouponReview(true));
    } catch (error) {
      dispatch(setErrorBook(error.toString()));
      dispatch(setLoadingBook(false));
      dispatch(setUpdatePriceReview(false));
      if (
        error?.response?.status === SESSION_EXPIRED_STATUS &&
        error?.response?.data[zeroItem] === SESSION_SEARCH_EXPIRED_MESSAGE
      ) {
        dispatch(setExpiredSession(true));
      }
    }
  };
};

export const getBookPackageWithCard = (bookWithCard: IBookPackageWithCard): AppThunk => {
  return async (dispatch, getState) => {
    dispatch(setLoadingBooking(true));
    dispatch(setExpiredSession(false));
    try {
      const postData: IBookPackageWithCard = {
        ...bookWithCard,
        guests: bookWithCard.guests.map((guest: IGuestsBooking) => {
          return {
            ...guest,
            surname: guest.surname.trim(),
            givenName: guest.givenName.trim(),
          };
        }),
      };

      const res = await axiosInstance.post(Urls.BookPackageWithCard, postData, {
        ...getHeaders(),
      });

      if (res?.data && res?.data[zeroItem]?.errorCode === SOLD_OUT) {
        dispatch(setSoldOutError(true));
      }

      if (res?.data && res?.data?.hotelSegments[zeroItem].status === STATUS_OK) {
        dispatch(setBookingComplete(true));
        localStorage.removeItem(HOTEL_BOOKING_GUEST_INFO_LABEL);
        localStorage.removeItem(HOTEL_BOOKING_ADDRESS_INFO_LABEL);

        const { navigationMenuStore } = getState();

        if (navigationMenuStore?.items?.promo) {
          UrlUtils.removeFromUrl(SESSION_KEY_LABEL);
          UrlUtils.removeFromUrl(ROOMS_SEARCH_LABEL);
          UrlUtils.removeFromUrl(SESSION_KEY_LABEL_ROOMS);
        }
      } else {
        dispatch(setBookingInProgress(true));
      }

      dispatch(setLoadingBooking(false));
      if (isEmpty(res.data.errors)) {
        localStorage.setItem(HOTEL_CONFIRMED_INFO, JSON.stringify(res.data));
        localStorage.setItem(HOTEL_BOOKING_COMPLETE, JSON.stringify(true));
      }
      dispatch(setBooking(res.data));
    } catch (error) {
      dispatch(setErrorBooking(error.toString()));
      dispatch(setLoadingBooking(false));

      if (error?.response?.data && error?.response?.data[zeroItem].errorType === VALIDATION_ERROR) {
        dispatch(setErrorsField(error.response.data));
      } else if (
        error?.response?.data &&
        error?.response?.data[zeroItem].errorType === EXTERNAL_ERROR
      ) {
        dispatch(setExternalError(true));
      } else if (
        error?.response?.data &&
        error?.response?.data[zeroItem].errorCode === PRICE_CHANGED
      ) {
        dispatch(setPriceChangedError(true));
      } else if (error?.response?.data && error?.response?.data[zeroItem].errorCode === SOLD_OUT) {
        dispatch(setSoldOutError(true));
      } else if (error?.response?.data && error?.response?.data[zeroItem].errorType === ERROR) {
        dispatch(setErrorsBookingMessage(error.response.data));
      } else if (error?.response?.data[zeroItem].errorCode === SAVING_MISMATCH) {
        dispatch(setExpiredSession(true));
        dispatch(setSavingsMismatch(true));
      }
    }
  };
};
